import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'

import Dialog from './Dialog'
import InputField from './InputField'
import DatesPicker from './DatesPicker'
import GridCheckbox from './GridCheckbox'
import GridIconButton from './GridIconButton'
import SortableTableHead from './SortableTableHead'
import { colours, titlesImpostazioni } from '../settings/settings'
import {
  fromDateToString,
  fromStringToDate,
  formatBool,
  formatBodyField,
} from '../hooks/utils-functions'
import {
  useGetCorsiQuery,
  useCreateCorsoMutation,
  useUpdateCorsoMutation,
  useDeleteCorsoMutation,
} from '../hooks/api-slice'

const columnsCorsi = [
  { label: 'Stagione', sortable: false },
  { label: 'Nome', sortable: false },
  { label: 'Date', sortable: false },
  { label: 'Indirizzo', sortable: false },
  { label: 'Iscr. aperte', sortable: false },
  { label: 'Opzioni', sortable: false },
]

const DialogCorsi = ({ open, handleClose }) => {
  const { data: corsi } = useGetCorsiQuery()
  const [createCorsoMutation] = useCreateCorsoMutation()
  const [updateCorsoMutation] = useUpdateCorsoMutation()
  const [deleteCorsoMutation] = useDeleteCorsoMutation()

  const isMobile = useSelector(state => state.home.isMobile)

  const [editingCorso, setEditingCorso] = useState()
  const [addingCorso, setAddingCorso] = useState()
  const [newCorso, setNewCorso] = useState()

  const cSetNewCorso = (field, value) =>
    setNewCorso({ ...newCorso, [field]: formatBodyField(value) })

  const fValue = i => i?.map(i => fromStringToDate(i)) || []
  const fOnChange = i =>
    cSetNewCorso(
      'date',
      i?.sort((a, b) => a - b)?.map(x => fromDateToString(new Date(x))) || [],
    )

  const clearNewCorso = () => {
    setEditingCorso()
    setAddingCorso()
    setNewCorso()
  }

  useEffect(() => {
    clearNewCorso()
  }, [open])

  const addNewCorso = async () => {
    const result = await createCorsoMutation(newCorso)
    if (!result.error) clearNewCorso()
  }

  const editCorso = async id => {
    const result = await updateCorsoMutation({ id, ...newCorso })
    if (!result.error) clearNewCorso()
  }

  const deleteCorso = async id => {
    await deleteCorsoMutation(id)
  }

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      title={titlesImpostazioni.titleCorsi}
      stretchWidth
      stretchHeight
    >
      {!corsi ? (
        <h4>Corsi non trovati.</h4>
      ) : (
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={12}>
            Il presente modulo consente di gestire le indizioni dei corsi di
            formazione. Impostando le iscrizioni come aperte, si darà la
            possibilità alle società di effettuare le iscrizioni.
          </Grid>
          <Grid container alignItems='center' sx={{ width: '100%' }}>
            {(!!addingCorso && (
              <>
                <Grid size={{ xs: 12, md: 3 }}>
                  <center>
                    <InputField
                      id='nome'
                      value={newCorso?.nome}
                      onChange={i =>
                        cSetNewCorso('nome', formatBodyField(i, 'upper'))
                      }
                      autoFocus
                      label={columnsCorsi[1].label}
                    />
                  </center>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <center>
                    <DatesPicker
                      id='date'
                      label={columnsCorsi[2].label}
                      value={fValue(newCorso?.date)}
                      onChange={fOnChange}
                      mode='add'
                      isMobile={isMobile}
                    />
                  </center>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <center>
                    <InputField
                      id='indirizzo'
                      value={newCorso?.indirizzo}
                      onChange={i =>
                        cSetNewCorso('indirizzo', formatBodyField(i, 'upper'))
                      }
                      label={columnsCorsi[3].label}
                    />
                  </center>
                </Grid>
                <GridIconButton
                  type='save'
                  size={{ xs: 6, md: 1 }}
                  onClick={addNewCorso}
                />
                <GridIconButton
                  type='clear'
                  size={{ xs: 6, md: 1 }}
                  onClick={clearNewCorso}
                />
              </>
            )) || (
              <GridIconButton
                type='add'
                size={12}
                onClick={() => {
                  setEditingCorso()
                  setAddingCorso(true)
                  setNewCorso({})
                }}
                text='Nuovo corso'
              />
            )}
          </Grid>
          {(corsi.length === 0 && <h4>Nessun corso trovato.</h4>) || (
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead table={corsi} columns={columnsCorsi} />
                <TableBody>
                  {corsi.map((c, index) => {
                    return (
                      <TableRow
                        key={index}
                        style={{ backgroundColor: colours.white }}
                      >
                        {(editingCorso === c._id && (
                          <>
                            <TableCell padding='checkbox'>
                              {c.stagione}
                            </TableCell>
                            <TableCell padding='checkbox'>
                              <InputField
                                id='nome'
                                value={newCorso?.nome}
                                onChange={i =>
                                  cSetNewCorso(
                                    'nome',
                                    formatBodyField(i, 'upper'),
                                  )
                                }
                                autoFocus
                                label={columnsCorsi[1].label}
                              />
                            </TableCell>
                            <TableCell padding='checkbox'>
                              <DatesPicker
                                id='date'
                                label={columnsCorsi[2].label}
                                value={fValue(newCorso?.date)}
                                onChange={fOnChange}
                                mode='update'
                                isMobile={isMobile}
                              />
                            </TableCell>
                            <TableCell padding='checkbox'>
                              <InputField
                                id='indirizzo'
                                value={newCorso?.indirizzo}
                                onChange={i =>
                                  cSetNewCorso(
                                    'indirizzo',
                                    formatBodyField(i, 'upper'),
                                  )
                                }
                                label={columnsCorsi[3].label}
                              />
                            </TableCell>
                            <TableCell padding='checkbox'>
                              <GridCheckbox
                                value={!!newCorso?.iscrizioniAperte}
                                onChange={(_, x) =>
                                  cSetNewCorso('iscrizioniAperte', x)
                                }
                                md={12}
                              />
                            </TableCell>
                            <TableCell padding='checkbox'>
                              <Grid container sx={{ width: '100%' }}>
                                <GridIconButton
                                  type='save'
                                  onClick={() => editCorso(c.id)}
                                />
                                <GridIconButton
                                  type='clear'
                                  onClick={clearNewCorso}
                                />
                              </Grid>
                            </TableCell>
                          </>
                        )) || (
                          <>
                            <TableCell>{c.stagione}</TableCell>
                            <TableCell>{c.nome}</TableCell>
                            <TableCell>{c.date?.join(', ')}</TableCell>
                            <TableCell>{c.indirizzo}</TableCell>
                            <TableCell>
                              {formatBool(c.iscrizioniAperte)}
                            </TableCell>
                            <TableCell>
                              <Grid container sx={{ width: '100%' }}>
                                <GridIconButton
                                  type='edit'
                                  onClick={() => {
                                    setEditingCorso(c._id)
                                    setAddingCorso()
                                    setNewCorso(c)
                                  }}
                                />
                                <GridIconButton
                                  type='delete'
                                  onClick={() => deleteCorso(c.id)}
                                />
                              </Grid>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      )}
    </Dialog>
  )
}

export default DialogCorsi
